import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fiapPreviewSchema } from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { TextField } from '../../components/TextField';
import styles from '../competitions/Competitions.module.css';
import { errorMessage } from '../../i18n/errors';

/**
 * Export für den FIAP-Antrag: Zeitraum (nach Entscheidungsdatum) und Obergrenze wählen, Vorschau mit
 * Warnungen prüfen, dann CSV oder das befüllte Original-Formular herunterladen. Zusätzlich der Export aller
 * Einreichungen (auch Misserfolge) für eigene Auswertungen.
 */
export function ExportPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cap, setCap] = useState(true);

  const query = new URLSearchParams({ cap: String(cap) });
  if (from) query.set('from', from);
  if (to) query.set('to', to);
  const preview = useQuery({
    queryKey: ['export', 'preview', query.toString()],
    queryFn: () =>
      apiRequest('GET', `/exports/fiap-application?${query.toString()}`, {
        schema: fiapPreviewSchema,
      }),
  });
  const data = preview.data;

  return (
    <section>
      <h1>{t('export.title')}</h1>
      <p>{t('export.intro')}</p>
      <div className={styles.toolbar}>
        <TextField
          label={t('export.from')}
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <TextField
          label={t('export.to')}
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
        <label className={styles.check}>
          <input type="checkbox" checked={cap} onChange={(event) => setCap(event.target.checked)} />
          {t('export.cap')}
        </label>
      </div>
      {preview.isPending && <p role="status">{t('common.loading')}</p>}
      {preview.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, preview.error)}
        </p>
      )}
      {data && (
        <>
          <p role="status">{t('export.rows', { count: data.rows.length })}</p>
          {data.exceedsCapacity && (
            <p role="alert" className={styles.error}>
              {t('export.exceeds', { capacity: data.capacity })}
            </p>
          )}
          {data.dropped.length > 0 && <p>{t('export.dropped', { count: data.dropped.length })}</p>}
          {data.warnings.length > 0 && (
            <div className={styles.warnings} role="status">
              <p>{t('export.warnings', { count: data.warnings.length })}</p>
              <ul>
                {data.warnings.map((warning, index) => (
                  <li key={`${warning.label}-${warning.problem}-${index}`}>
                    {warning.label}: {t(`export.problem.${warning.problem}`)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={styles.actions}>
            <a href={apiUrl(`/exports/fiap-application.csv?${query.toString()}`)}>
              {t('export.downloadCsv')}
            </a>
            {!data.exceedsCapacity && (
              <a href={apiUrl(`/exports/fiap-application.xlsx?${query.toString()}`)}>
                {t('export.downloadXlsx')}
              </a>
            )}
            <a href={apiUrl('/exports/submissions.csv')}>{t('export.downloadAll')}</a>
          </div>
          {data.rows.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Acc. N°</th>
                  <th>Title N°</th>
                  <th>{t('export.colTitle')}</th>
                  <th>{t('export.colSalon')}</th>
                  <th>{t('export.colCountry')}</th>
                  <th>FIAP</th>
                  <th>Award</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.accNo}>
                    <td>{row.accNo}</td>
                    <td>{row.titleNo}</td>
                    <td>{row.title}</td>
                    <td>{row.salon}</td>
                    <td>{row.country}</td>
                    <td>{row.fiapNumber}</td>
                    <td>{row.award}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
